"use client";

import { type MonthRowLayout } from "../event-layout";

import { EventBar } from "./event-bar";
import { EventDayBlock } from "./event-day-block";
import { MoreEventsButton } from "./more-events-button";

// =============================================================================

/** Height of one lane (bars grid rows, block budget), in rem */
export const LANE_REM = 1.5;

type MonthWeekLanesProps = {
  week: Date[];
  layout: MonthRowLayout;
  /** Room kept above the events for the day number (and the addon) */
  topRem: number;
  /** Minutes a whole day weighs: blocks only fill their share of the cell */
  windowMinutes: number;
};

/**
 * Event overlay of a month week row: multi-day bars in the top lanes, then
 * single-day blocks splitting the rest of each cell by duration, then "+N
 * more". Pointer events pass through to the day cells around the events.
 */
export function MonthWeekLanes({
  week,
  layout,
  topRem,
  windowMinutes,
}: MonthWeekLanesProps) {
  const { bars, barLanesByCol, blocksByCol, hiddenByCol } = layout;
  const gridTemplateColumns = `repeat(${week.length}, minmax(0, 1fr))`;
  const hasBlocks = week.some(
    (_, col) => blocksByCol[col].length > 0 || hiddenByCol[col].length > 0
  );

  return (
    <>
      {bars.length > 0 && (
        <div
          className="pointer-events-none absolute inset-x-0 grid auto-rows-6"
          style={{ top: `${topRem}rem`, gridTemplateColumns }}
        >
          {bars.map((segment) => (
            <EventBar
              key={segment.event.id}
              segment={segment}
              showTime={!segment.event.allDay && segment.isStart}
              compactOnMobile
            />
          ))}
        </div>
      )}
      {hasBlocks && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-1.5 grid"
          style={{ top: `${topRem}rem`, gridTemplateColumns }}
        >
          {week.map((day, col) => {
            const blocks = blocksByCol[col];
            const hidden = hiddenByCol[col];
            // Unused share of the day: keeps short events short and the
            // empty part of the cell clickable
            const freeWeight =
              windowMinutes -
              blocks.reduce((total, block) => total + block.weight, 0);
            return (
              <div
                key={day.getTime()}
                className="flex min-h-0 min-w-0 flex-col gap-0.5 overflow-hidden px-1"
                style={{ paddingTop: `${barLanesByCol[col] * LANE_REM}rem` }}
              >
                {blocks.map((block) => (
                  <EventDayBlock key={block.event.id} block={block} />
                ))}
                {blocks.length > 0 && freeWeight > 0 && (
                  <div
                    aria-hidden
                    style={{ flexBasis: 0, flexGrow: freeWeight }}
                  />
                )}
                {hidden.length > 0 && (
                  <MoreEventsButton day={day} events={hidden} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
